// React.
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// Third-parties.
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import TableCSVExport from '../../../component/TableCSVExport';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import { SqlStatisticsSummary } from '../../../types/instance/SqlStatistics';

// Components.
import Alert from "../../Alert";
import ReactTable from "../../ReactTable";
import StatementToolTip from "./StatementToolTip";

// Helper.
import Helper from '../../../helpers/Helper';
import { capitalizeString, fetchResults, getDefaultPageSize, getToolTipPosition, } from '../../../helpers/utils';
import SqlApi from '../../../api/instance/SqlApi';
import { showError } from '../../../utils/showAlert';
import { getAPIString, getStatementTypeFromURL, StatementTypeInfo } from "../../../views/instances/tabs/utils";

// Constants.
import { CHART_COLOURS_STATEMENTS, INSTANCE_TYPE_COCKROACHDB } from "../../Constants";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import { StatementSidePanelContext } from "../../../context/StatementSidePanelContext";
import ActivityApi from '../../../api/instance/ActivityApi';
import SearchInput from '../../SearchInput';

export default function StatementSqlStatisticsTable(props:
    {
        instance: InstanceTarget,
        statementId?: string,
        batchStatementId?: string,
        filterParameters?: string,
        statementType?: StatementTypeInfo,
        onStatementClick?: (statementId: string, statementType?: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch') => void
    }) {
    const [sqlStatistics, setSqlStatistics] =
        useState<SqlStatisticsSummary[]>([]);
    const [supportedStatistics, setSupportedStatistics] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [statements, setStatements] = useState<string[]>([]);
    const [visibleToolTip, setVisibleToolTip] = useState({
        id: '',
        position: 0
    });

    const isCockroachDb = props.instance.type === INSTANCE_TYPE_COCKROACHDB
    const hiddenColumnsList = isCockroachDb ? ['logical_writes', 'logical_reads', 'physical_reads', 'cpu_time_millisconds'] : []
    const timeRangeContext = useContext(TimeRangeContext)
    const isInStatementSidePanel = useContext(StatementSidePanelContext);
    // Hide icons if we're in a side panel AND no onStatementClick is provided
    const hideStatementIcons = isInStatementSidePanel && !props.onStatementClick;
    const statementType = props.statementType || getStatementTypeFromURL()
    const sqlHashString = statementType.isGroup ? 'grouphash' : 'sqlhash'
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());


    const extraColumns = [
        {
            label: 'CPU Time',
            key: 'cpu_time_milliseconds'
        },
        {
            label: 'Physical Reads',
            key: 'physical_reads'
        },
        {
            label: 'Logical Reads',
            key: 'logical_reads'
        },
        {
            label: 'Logical Writes',
            key: 'logical_writes'
        }]

    // CSV headers
    const headers: any[] = useMemo(() => {
        const defaultHeaders = [
            {
                label: 'SQL Hash',
                key: sqlHashString
            },
            {
                label: 'Statement Text',
                key: 'sqltext'
            }
        ]
        supportedStatistics.forEach(item => {
            defaultHeaders.push({
                label: capitalizeString(item),
                key: item
            })
        })

        // Add extra columns for non-CockroachDB instances
        if (!isCockroachDb) {
            defaultHeaders.push(...extraColumns);
        }

        return defaultHeaders
    }, [supportedStatistics, sqlHashString, isCockroachDb]);

    // Table columns
    const columns: Column[] = useMemo(() => {
        const defaultColumns = [
            {
                Header: '#',
                accessor: sqlHashString,
                headerClassName: 'text sql-statistics-col-hash',
                className: 'sql-statistics-col-hash',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic',
                Cell: ({ cell }: any) => {
                    // const hideTab = (props.instance.type === INSTANCE_TYPE_ORACLE || props.instance.type === INSTANCE_TYPE_DB2) ?  '' : '?hide=activity'
                    if (statementType.isGroup) return cell.row.values[sqlHashString];

                    return (
                        <Link to={`/instances/${props.instance.id}/activity/statement/${cell.row.values[sqlHashString]}/sql-statistics`}>
                            {props.onStatementClick && <i className="fal fa-arrow-up-right-from-square fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in new page" />} {cell.row.values[sqlHashString]}
                        </Link>
                    );
                }
            },
        {
            Header: 'Statement',
            accessor: 'sqltext',
            headerClassName: 'text sql-statistics-col-statement',
            className: 'sql-statistics-col-statement',
            canSort: true,
            sortDescFirst: false,
            sortType: 'basic',
            Cell: ({ cell }: any) => {
                const link = `/instances/${props.instance.id}/activity/statement/${cell.row.values[sqlHashString]}/text`;
                const statementTypeForPanel = statementType.isGroup ? 'groupedstatement' : 'statement';

                // Has callback: show icon + click opens panel
                if (props.onStatementClick) {
                    return (
                        <span
                            style={{ cursor: 'pointer', color: '#0d6efd' }}
                            onClick={(e) => {
                                e.preventDefault();
                                props.onStatementClick!(cell.row.values[sqlHashString], statementTypeForPanel);
                            }}
                        >
                            <i className="fal fa-sidebar-flip fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in side panel" />
                            {cell.row.values.sqltext.substring(0, 200)}
                        </span>
                    );
                }

                // No callback: just link, no icon
                return <Link to={link}>{cell.row.values.sqltext.substring(0, 200)}</Link>;
            }
        },
            {
                Header: 'Duration',
                accessor: 'duration',
                headerClassName: 'text-end sql-statistics-col-duration',
                className: 'text-end sql-statistics-col-duration',
                canSort: true,
                sortDescFirst: true,
                sortType: 'basic',
                Cell: ({ cell }: any) =>
                    (Helper.getTimeInEnglish(cell.row.values.duration))
            },
            {
                Header: 'Executions',
                accessor: 'executions',
                headerClassName: 'text-end sql-statistics-col-executions',
                className: 'text-end sql-statistics-col-executions',
                canSort: true,
                sortDescFirst: true,
                sortType: 'basic',
                Cell: ({ cell }: any) =>
                    (Helper.getFormattedNumber(cell.row.values.executions))
            },
            {
                Header: 'Avg. Duration',
                accessor: 'average_duration',
                headerClassName: 'text-end sql-statistics-col-avg-duration',
                className: 'text-end sql-statistics-col-avg-duration',
                canSort: true,
                sortType: 'basic',
                sortDescFirst: true,
                Cell: ({ cell }: any) => cell.row.values.average_duration ? Helper.getTimeInEnglish(cell.row.values.average_duration) : ''
            },
        ]
        supportedStatistics.forEach(item => {
            if (item !== 'executions' && item !== 'duration') {
                defaultColumns.push({
                    Header: capitalizeString(item),
                    accessor: item,
                    headerClassName: 'text-end sql-statistics-extra-col',
                    className: 'text-end sql-statistics-extra-col',
                    canSort: true,
                    sortDescFirst: true,
                    sortType: 'basic',
                    Cell: ({ cell }) => {
                        const key = cell.column.id;
                        const value = cell.row.values[key];

                        const isTime = key.includes('time');

                        return value
                            ? isTime
                                ? Helper.getTimeInEnglish(value)
                                : Number(value).toFixed(2).replace(/\.?0+$/, '')
                            : '0';
                    }
                })
            }
        })
        return defaultColumns
    },
        [
            props.instance.id,
            props.instance.type,
            supportedStatistics,
            sqlHashString,
            statementType.isGroup,
            hideStatementIcons,
            props.onStatementClick,
        ]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    useEffect(() => {
        async function getStatements() {
            const apiString = getAPIString(props.statementId, props.batchStatementId, statementType)
            // Get statements.
            ActivityApi.getDimension('sql', buildApiQuery(timeRangeContext.timeRange), props.instance.id, `${apiString}${props.filterParameters}`)
                .then((response: { data: any[]; }) => {

                    let statementsList = [];

                    // Build top statements pie chart.
                    for (let index = 0; index < response.data.length; index++) {

                        if (index < CHART_COLOURS_STATEMENTS.length) {
                            // Set the top colours for the chart.
                            response.data[index].color = CHART_COLOURS_STATEMENTS[index];
                        }
                        statementsList.push(response.data[index][sqlHashString]);
                    }
                    setStatements(statementsList)
                })
        }
        void getStatements()
    },
        [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.instance.id, props.instance.type,
            props.statementId]);

    useEffect(() => {
        async function load() {
            try {
                setLoading(true);
                const apiString = statementType.isGroup ? 'group' : 'sql'
                const results: any[][] = await fetchResults(
                    [
                        SqlApi.fetchStatistic(apiString, buildApiQuery(timeRangeContext.timeRange), props.instance.id, props.instance.type, `&sort=duration+desc&${sqlHashString}=${props.batchStatementId || props.statementId}`)
                    ]);
                const supportedStatistics: any[][] = await fetchResults(
                    [
                        SqlApi.fetchStatisticName(props.instance.type, '&collect=true')
                    ]);
                if (active) {
                    const resultsWithAverage = Helper.addAverageAndColor('duration', 'executions', results[0], 0)
                    // setSupportedStatistics(supportedStatistics[0]);
                    // let filteredList: any[] = []
                    // if (resultsWithAverage.length) {
                    //
                    // }
                    // resultsWithAverage.forEach(item => {
                    //     if (statements.includes(item.sqlhash)){
                    //         filteredList.push(item)
                    //     }
                    // })
                    // // const filteredList = props.statementId ? resultsWithAverage.filter(item => item.sqlhash === props.statementId) : resultsWithAverage
                    //
                    // setSqlStatistics(filteredList);
                    // setLoading(false);
                    setSupportedStatistics(supportedStatistics[0]);
                    // const filteredList = props.statementId ? resultsWithAverage.filter(item => item[sqlHashString] === props.statementId) : resultsWithAverage
                    setSqlStatistics(resultsWithAverage);
                    setLoading(false);
                }
            } catch (x: any) {
                console.log(x.message);
                const errorMessage = x.message || x.toString();
                setError(errorMessage);
                showError('Failed to load statement SQL statistics', errorMessage);
                setLoading(false);
                setSqlStatistics([]);
            }
        }

        let active: boolean = true;
        load();
        return () => {
            active = false;
        }
    },
        [statements]);

    const data = useMemo(() =>
        sqlStatistics.filter((row) =>
            row.sqltext.toLowerCase().includes(tableFilter.toLowerCase()) ||
            // @ts-ignore
            row[sqlHashString].toLowerCase().includes(tableFilter.toLowerCase())),
        [sqlStatistics, tableFilter]);

    // Transform function to escape double quotes in SQL text for CSV export
    const transformCsvData = useMemo(() => (csvData: any[]) => {
        const transformed = JSON.parse(JSON.stringify(csvData));
        transformed.forEach((item: any) => {
            if (item.sqltext) {
                item.sqltext = item.sqltext.replaceAll('"', '""');
            }
        });
        return transformed;
    }, []);

    const tableInstance = useTable(
            {
                columns,
                data,
                initialState:
                {
                    hiddenColumns: hiddenColumnsList,
                    pageIndex: initialPageIndex,
                    pageSize: initialPageSize,
                },
                disableSortRemove: true,
                autoResetPage: false,
                autoResetSortBy: false,
                autoResetFilters: false,
                autoResetSelectedRows: false,
                autoResetExpanded: false,
                autoResetGroupBy: false,
            }, useSortBy, usePagination);

    const
        {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            prepareRow,
            page,
            setPageSize,
            pageOptions,
            pageCount,
            canPreviousPage,
            previousPage,
            canNextPage,
            nextPage,
            gotoPage,
            state: { pageIndex, pageSize }
        } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    function clearTableFilter() {
        setTableFilter('');
    }

    // The bracket must be present and on the same line as the return.
    return (
        <div className="card">
            <div className="card-header">
                <i className="fal fa-scroll fa-fw" aria-hidden="true" />
                {statementType.isGroup ? 'Grouped Statement' : 'Statements'}
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                    data-bs-target="#collapseStatements" aria-expanded="false" aria-controls="collapseStatements" />
            </div>
            <div id="collapseStatements" className="card-body collapse show">
                {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 &&
                    (<Alert
                        message={`To help preserve performance, we limit the total number of statement records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by the number of executions.`}
                        heading="Statement records" variant="alert-info" />)}
                <div className="row row-cols-1 row-cols-md-2 table-search">
                    <div className="col col-md-10">
                        <TableCSVExport
                            tableInstance={tableInstance}
                            filename="SQL Statistics"
                            transformData={transformCsvData}
                            headers={headers}
                        />
                    </div>
                    <div className="col col-md-2">
                        <SearchInput
                            ref={inputRef}
                            value={tableFilter}
                            onChange={(e) => setTableFilter(e.target.value)}
                            onClear={clearTableFilter}
                        />
                    </div>
                </div>
                <div className="sql-statistics-table-wrapper">
                <ReactTable
                    getTableProps={getTableProps}
                    getTableBodyProps={getTableBodyProps}
                    headerGroups={headerGroups}
                    prepareRow={prepareRow}
                    page={page}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageOptions={pageOptions}
                    pageCount={pageCount}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    setPageSize={setPageSize}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    dataLength={data.length}
                    error={error}
                    loading={loading}
                    renderCell={(cell) => {
                        if (cell.column.id === "sqltext") {
                            const hashValue = cell.row.values[sqlHashString];
                            return (
                                <div className='tooltip-scroll ellipsis'
                                    onMouseEnter={(event) => setVisibleToolTip({
                                        id: hashValue,
                                        position: getToolTipPosition(event)
                                    })}
                                    onMouseLeave={() => setVisibleToolTip({ id: '', position: 0 })}
                                >
                                    {cell.render('Cell')}
                                    {hashValue === visibleToolTip.id && (
                                        <StatementToolTip instanceType={props.instance.type}
                                            position={visibleToolTip.position}
                                            id={hashValue}
                                            link={`/instances/${props.instance.id}/activity/statement/${hashValue}/text`}
                                            onClick={props.onStatementClick ? () => props.onStatementClick!(hashValue, statementType.isGroup ? 'groupedstatement' : 'statement') : undefined}
                                            cell={cell} />
                                    )}
                                </div>
                            );
                        }
                        return cell.render('Cell');
                    }}
                />
                </div>
            </div>
        </div>
    )
}
